import authenticatedWrapper from "../layout/authenticated-wrapper.html";
import unauthenticatedWrapper from "../layout/unauthenticated-wrapper.html";
import {
	DASHBOARD,
	FORM_REPORT,
	BASE_REPORT,
	NEW_REPORT,
	EDIT_REPORT, 
	VIEW_REPORTS,
	ENTITIES_SCREEN,
	USERS_SCREEN,
	ANALYTICS_SCREEN,
	OPERATIONS_SCREEN,
	REPORTING_SCREEN,
	NOTICES_SCREEN
} from "routes.js";
import {
	EVENT_NEW_FORM,
	openIdCallbackSuffix,
	openIdSilentRenewPath,
	maxAuthClockSkewSeconds
} from "constants.js";
import { forEach } from "lodash";
import moment from "moment";
import { EXPORTS_SCREEN } from "../routes";
import { isAnyTypeWritePermittedScope } from "../services/security/commonScopes";
import {
	anyOf, 
	globs 
} from "../services/globs";
import {
	Pattern, ActionOrPattern, Any 
} from "../services/security/actionScopes";
import {
	promptForDirtyFormRefresh,
	showNotConnectedOnRefreshError
} from "../components/manage-report/prompts";

function clearSplash(deferral) {
	const loader = angular.element(document.querySelector("#vesl-splash"));

	loader.addClass("fadeOut");

	setTimeout(function() {
		loader.remove();
	}, 1150);

	deferral.resolve();
}

export default function routeConfig($stateProvider, $urlRouterProvider, $locationProvider) {
	const canViewAnalytics = new Pattern(anyOf(globs.proxies.byType("ANALYTICS").self));
	const canViewQueries = new Pattern(anyOf(globs.proxies.byType("QUERY").self));
	const canViewAnalyticsOrQueries = new Any([canViewAnalytics, canViewQueries]);

	const canViewOperations = new Pattern(anyOf(globs.proxies.byType("OPERATIONS").self));
	const canViewReporting = new Pattern(anyOf(globs.proxies.byType("REPORTING").self));
			
	$stateProvider
		.state("unauthenticated", {
			abstract: true,
			url: "",
			template: unauthenticatedWrapper,
			data: {
				bodyClassName: "login-content"
			},
			resolve: {
				logout($userProfile, appState) {
					$userProfile.clear();
					appState.reset();
				},
				splash: function($q) {
					const deferral = $q.defer();

					// handle unauthenticated route specific things here
					clearSplash(deferral);

					return deferral.promise;
				}
			}
		})
		.state("authenticated", {
			abstract: true,
			url: "",
			template: authenticatedWrapper,
			resolve: {
				apiState: function(api, appState, $logger) {
					if(appState.apiState) {
						return appState.apiState;
					}

					return api.get("").then((rootResponse) => {
						const apiMoment = moment(rootResponse.ServerTime);
						const localMoment = moment();

						rootResponse.skewSeconds = apiMoment.isValid()
							? localMoment.diff(apiMoment, "seconds")
							: 0;
					
						$logger.info(`API state retrieved.  Server time: ${apiMoment.format()}, Local time: ${localMoment.format()}, Skew seconds: ${rootResponse.skewSeconds}`);

						rootResponse.skew = rootResponse.skewSeconds / 60;

						appState.apiState = rootResponse;
						return rootResponse;
					});
				},
				user: function($state, openIdService, auth, apiState, toastr, $logger) {
					if(Math.abs(apiState.skewSeconds) > maxAuthClockSkewSeconds) {
						const skewMinutes = Math.ceil(Math.abs(apiState.skewSeconds) / 60);
						toastr.error("Local time is not within 5 minutes of server time.  Your device may have an inaccurate time");
						$logger.error(`Local time is ${skewMinutes} minutes ${apiState.skewSeconds > 0 ? "more than" : "less than"} server time.`);

						throw `Local time is ${skewMinutes} minutes ${apiState.skewSeconds > 0 ? "more than" : "less than"} server time.
						 \nLocal time should be within 5 minutes of server time.
						 \nPlease adjust your computer's date, clock, or timezone as needed.
						 \nThen, close and re-open your browser, and try again.`;
					}

					const fromRouteName = $state.transition.to().name;
					const userManager = openIdService.getUserManager();
					const fromRouteParams = {};

					forEach($state.transition.params(), (p, key) => {
						if(p !== undefined && p !== null) {
							fromRouteParams[key] = p;
						}
					});

					return userManager.getUser().then((userInfo) => {
						if(userInfo === null) {
							// this returns a promise; will reject if auth server is down
							const signin = openIdService.beginSigninRedirect(fromRouteName, fromRouteParams).catch((error) => {
								$logger.exception(error, "Authentication redirect failed");
								throw openIdService.getAuthenticationFailureMessage(error);
							});
							return signin;
						}

						auth.setAuthData(userInfo);

						return userInfo;
					});
				},
				forms(formsService, formService, loadingService, $userProfile, user, $state, $logger) {
					if(!$userProfile.getUser() && !user) {
						return new Promise((resolve, reject) => {
							setTimeout(() => {
								reject("user does not exist in profile service");
							}, 15000);
						});
					}

					const promise = formsService.getForms()
						.then(selectedForm => {
							if(
								selectedForm === undefined
								|| (selectedForm.length === 0
									&& $state.transition.targetState().name() != "authenticated.welcome")
							) {
								$state.transition.abort();
								$state.go("authenticated.welcome");
							}

							return selectedForm;
						});

					return loadingService.watch(promise, "Loading...");
				},
				splash: function($q, user, forms, $logger) {
					const deferral = $q.defer();

					Promise.all([user, forms])
						.finally(() => {
							clearSplash(deferral);
						});

					return deferral.promise;
				}
			},
			params: {
				via: null
			}
		})
		.state("unauthenticated.privacy", {
			title: "Privacy Policy",
			url: "/privacy",
			component: "privacy"
		})
		.state("unauthenticated.deletion", {
			title: "Account Deletion",
			url: "/account-deletion",
			component: "deletion"
		})
		.state("unauthenticated.failed", {
			title: "VESL",
			url: "/oops",
			component: "failedRoute",
			params: {
				errorMsg: null
			},
			resolve: {
				disallow: function($q, $state) {
					const { system } = $state.transition.targetState().params();
					if(!system) {
						$state.transition.abort();
						$state.go(DASHBOARD);
					}
				}
			}
		})
		.state("unauthenticated.oauth-login", {
			title: "Logging in",
			url: openIdCallbackSuffix,
			component: "oauthLogin"
		})
		.state("authentication", {
			abstract: true,
			url: "",
			template: "<ui-view></ui-view>"
		})
		.state("authentication.renew", {
			title: "VESL",
			url: openIdSilentRenewPath,
			component: "silentRenew"
		})
		.state("unauthenticated.oauth-splash", {
			title: "Login",
			url: "/loggingin",
			component: "oauthSplash"
		})
		.state("authenticated.dashboard", {
			title: "Dashboard",
			url: "/user/dashboard",
			component: "dashboard"
		})
		.state("authenticated.welcome", {
			title: "Welcome",
			url: "/welcome?{agency:string}&{form:string}",
			params: {
				form: { dynamic: true }
			},
			component: "welcome"
		})
		.state("authenticated.compliance", {
			title: "Compliance",
			url: "/compliance",
			component: "complianceReport",
			data: {
				permissionKeys: "Compliance"
			}
		})
		.state("authenticated.report-list", {
			title: "Reports",
			url: "/reportlist?{start:date}&{end:date}&{page:string}&{itemsPerPage:string}&{sort:string}&{status:string}&{entityIds}&{beginNumber:int}&{endNumber:int}",
			params: { entityIds: { array: true } },
			component: "reportList",
			reloadOnSearch: false,
			data: {
				permissionKeys: "History"
			}
		})
		.state(VIEW_REPORTS, {
			title: "View Reports",
			url: "/reports/view?{sort:string}&{direction:string}",
			params: {
				ids: null
			},
			component: "viewReports",
			reloadOnSearch: false
		})
		.state(BASE_REPORT, {
			abstract: true,
			url: "/report",
			data: {
				permissionKeys: "Reporting"
			},
			resolve: {
				async dirtyFormRefresh(appState, $window) {
					if(!appState.forms.isDirty) {
						return;
					}

					const shouldRefresh = await promptForDirtyFormRefresh();

					if(shouldRefresh) {
						$window.location.reload();
					}
				},
				async staleFormRefresh(appState, $logger) {
					const now = new Date();

					const isStale = (!!appState.staleTimeoutAt && appState.staleTimeoutAt <= now)
						|| (!!appState.invalidTimeoutPadded && appState.invalidTimeoutPadded <= now);
					
					if(!isStale) {
						return;
					}

					try {
						await appState.reloadForm();
					}
					catch(err) {
						$logger.exception(err, "form reload failed");
						
						return showNotConnectedOnRefreshError();
					}
				}
			}
		})
		.state(NEW_REPORT, {
			title: "New Report",
			url: "",
			component: "newReport",
			params: {
				report: null
			},
			resolve: {
				report($stateParams) {
					if($stateParams.report) {
						return $stateParams.report;
					}

					return undefined;
				}
			}
		})
		.state(EDIT_REPORT, {
			title: "Edit Report",
			url: "?{id:int}",
			component: "editReport",
			params: {
				report: null
			},
			resolve: {
				report($stateParams, appState, api, loadingService, $state, eventBus, toastr, $logger) {
					if($stateParams.report) {
						return $stateParams.report;
					}

					const form = appState.forms.selected;
					const promise = api
						.get(FORM_REPORT(form.Id, $stateParams.id))
						.then((response) => {
							if(!response) {
								$logger.error("Form report response was falsy", {
									currentFormId: (appState.forms.selected || {}).Id,
									requestedFormId: form.Id,
									requestedReportId: $stateParams.id
								});
								throw new Error("Form report response was falsy");
							}

							if(response.IsNegative === true) {
								$state.transition.abort();
								$state.transitionTo(DASHBOARD, {}, { location: "replace", reload: DASHBOARD });
								toastr.error("You can't navigate to a negative report");
							}

							if(response.FormId !== form.Id) {
								return appState.forms.selectByFormId(response.FormId).then((newForm) => {
									if(newForm === undefined) {
										$state.transition.abort();
										$state.go("authenticated.welcome");
										
										return undefined;
									}
							
									eventBus.trigger(EVENT_NEW_FORM);
									return response;
								});
							}

							return response;
						})
						.catch(() => {
							if($state.transition && $state.transition.targetState().name() !== "unauthenticated.signin") {
								$state.transition.abort();

								if($state.transition.from().name === "") {
									$state.transitionTo(NEW_REPORT, {}, { location: "replace", reload: NEW_REPORT });
								}
							}
							return undefined;
						});

					return loadingService.watch(promise);
				}
			}
		})
		.state(ANALYTICS_SCREEN, {
			title: "Analytics",
			url: "/analytics?{id:string}&{search:string}&{page:string}&{itemsPerPage:string}&{sort:string}",
			component: "proxies",
			reloadOnSearch: false,
			data: {
				permissionKeys: "Administration>Analytics",
				actionScope: canViewAnalyticsOrQueries
			}
		})
		.state(OPERATIONS_SCREEN, {
			title: "Operations",
			url: "/operations?{id:string}&{search:string}&{page:string}&{itemsPerPage:string}&{sort:string}",
			component: "proxies",
			reloadOnSearch: false,
			data: {
				permissionKeys: "Administration>Operations",
				actionScope: canViewOperations
			}
		})
		.state(REPORTING_SCREEN, {
			title: "Reporting",
			url: "/reporting?{id:string}&{search:string}&{page:string}&{itemsPerPage:string}&{sort:string}",
			component: "proxies",
			reloadOnSearch: false,
			data: {
				permissionKeys: "Administration>Reporting",
				actionScope: canViewReporting
			}
		})
		.state(ENTITIES_SCREEN, {
			component: "entities",
			url: "/entities?{id:string}&{type:string}&{search:string}&{action:string}&{page:string}&{itemsPerPage:string}&{sort:string}",
			params: {
				mode: {
					value: null,
					dynamic: true,
					inherit: false
				}
			},
			title: "Entity Management",
			reloadOnSearch: false,
			data: {
				permissionKeys: "Administration>Entity",
				actionScope: isAnyTypeWritePermittedScope()
			}
		})
		.state(USERS_SCREEN, {
			title: "User Management",
			url: "/users?{id:string}&{search:string}&{action:string}&{page:string}&{itemsPerPage:string}&{sort:string}",
			component: "users",
			reloadOnSearch: false,
			data: {
				permissionKeys: "Administration>User"
			}
		})
		.state(EXPORTS_SCREEN, {
			component: "exports",
			url: "/exports",
			title: "Export Management"
		})
		.state(NOTICES_SCREEN, {
			component: "notices",
			url: "/notices?{id:string}&{search:string}&{action:string}&{page:string}&{itemsPerPage:string}&{sort:string}",
			title: "Notice Management",
			reloadOnSearch: false,
			data: {
				actionScope: new Any([
					new ActionOrPattern(globs.notices.create),
					new ActionOrPattern(globs.notices.update)
				])
			}
		});

	$urlRouterProvider.otherwise("/user/dashboard");
	$locationProvider.html5Mode({
		enabled: true,
		requireBase: false
	});
}
